import React, { useState, useMemo, useRef, useEffect } from "react";
import "./ItemSearch.css";
// Use standard icons from react-icons if available, or simple emojis for now
import { FaBoxOpen, FaMobileAlt, FaTags, FaTools, FaPlug } from "react-icons/fa";
import { BiNetworkChart } from "react-icons/bi";

const MAIN_CATEGORIES = [
  { label: "All Items", value: "All", icon: <FaBoxOpen className="me-1" /> },
  { label: "Devices", value: "Device", icon: <FaMobileAlt className="me-1" /> },
  { label: "Network", value: "Network Device", icon: <BiNetworkChart className="me-1" /> },
  { label: "Accessories", value: "Accessory", icon: <FaPlug className="me-1" /> },
  { label: "Parts", value: "Spare Part", icon: <FaTools className="me-1" /> },
];

const ItemField = ({ defaultInputValue = "", items, onAddItem }) => {
  const [query, setQuery] = useState(defaultInputValue);

  useEffect(() => {
    setQuery(defaultInputValue || "");
  }, [defaultInputValue]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [showBaseOnly, setShowBaseOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // console.log("items in itemsearchfiled ", items?.find(item => item.itemName?.toLowerCase().includes("asp")))

  const sortedOptions = useMemo(() => {
    let filtered = [...(items || [])];

    // 1. Filter by category
    if (activeCategory !== "All") {
      filtered = filtered.filter(item =>
        item.category === activeCategory
      );
    }

    // 2. Filter by Base Only
    if (showBaseOnly) {
      filtered = filtered.filter(item => !item.isVariant);
    }

    // 3. TOKENIZED TAG SEARCH LOGIC
    if (query && query.trim().length >= 2) {
      const searchTokens = query.toLowerCase().trim().split(/\s+/); // Split query by spaces

      filtered = filtered.filter(item => {
        // Create a searchable "blob" of text for this item
        const itemText = [
          item.itemName || "",
          item.manufacturer || "", // Search by Brand (MCDODO)
          item.category || "",     // Search by Category (Accessory)
          item.barcode || ""
        ].join(" ").toLowerCase();

        // Ensure EVERY token typed by user exists in the itemText
        // This allows "mcdd ada" to match "MCDODO ADAPTER"
        return searchTokens.every(token => itemText.includes(token));
      });
    }

    // 4. Sort logic
    return filtered.sort((a, b) => {
      const aName = a.itemName?.toLowerCase() || "";
      const bName = b.itemName?.toLowerCase() || "";

      if (query) {
        const q = query.toLowerCase();
        const aStarts = aName.startsWith(q);
        const bStarts = bName.startsWith(q);

        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;

        const aIndex = aName.indexOf(q);
        const bIndex = bName.indexOf(q);

        if (aIndex !== bIndex) return aIndex - bIndex;
      }

      return aName.localeCompare(bName);
    });
  }, [items, query, activeCategory, showBaseOnly]);

  const handleSelect = (item) => {
    setSelected(item);
    setQuery(item.itemName);
    setShowMenu(false);
    onAddItem(item);
  };

  return (
    <div className="item-search-container" ref={containerRef}>
      {/* Search Header with Filters & Base Toggle */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div className="d-flex flex-wrap gap-2">
          {MAIN_CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              type="button"
              className={`btn btn-sm rounded-pill px-3 py-1 border-0 shadow-sm transition-all ${activeCategory === cat.value ? 'btn-primary text-white' : 'btn-light text-muted'}`}
              onClick={() => {
                setActiveCategory(cat.value);
                setShowMenu(true);
              }}
              style={{ fontSize: '0.8rem', fontWeight: '600' }}
            >
              {cat.icon}
              {cat.label}
            </button>
          ))}
        </div>



        {/* Base Only Filter Toggle */}
        <button
          type="button"
          className={`btn btn-sm rounded-pill px-3 py-1 border-1 shadow-sm transition-all ${showBaseOnly ? 'btn-warning text-dark fw-bold border-warning' : 'btn-outline-secondary'}`}
          onClick={() => setShowBaseOnly(!showBaseOnly)}
          style={{ fontSize: '0.75rem' }}
        >
          {showBaseOnly ? "⭐ Only Base Models" : "Show All (Base + Variants)"}
        </button>
      </div>

      {/* Input */}
      <input
        type="text"
        className="rbt-input"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setShowMenu(true);
        }}
        onFocus={() => setShowMenu(true)}
        placeholder={`🔍 ${showBaseOnly ? 'Find model (e.g. iPhone 11)' : 'Search anything...'}`}
      />

      {/* Dropdown */}
      {showMenu && query.length >= 2 && sortedOptions.length > 0 && (
        <div className="search-results-menu">
          {sortedOptions.slice(0, 50).map((result, index) => (
            <div
              key={index}
              className="search-result-item"
              onClick={() => handleSelect(result)}
              style={!result.isVariant ? { borderLeft: '4px solid #f1c40f', background: '#fffef0' } : {}}
            >
              <img
                src={result.itemImage || "https://via.placeholder.com/40"}
                alt={result.itemName}
                className="item-image"
              />
              <div className="item-details">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="item-name">{result.itemName}</div>
                  {!result.isVariant && (
                    <span className="badge bg-warning text-dark" style={{ fontSize: '0.6rem', padding: '2px 6px' }}>MAIN MODEL</span>
                  )}
                </div>
                <div className="item-meta">
                  <div className="d-flex gap-1 align-items-center">
                    {result.isVariant ? (
                      <span className="text-info" style={{ fontSize: '0.7rem' }}>Variant</span>
                    ) : (
                      <span className="text-warning fw-bold" style={{ fontSize: '0.7rem' }}>New Batch Ready</span>
                    )}
                    <span className="mx-1">•</span>
                    <span className={result.isSerialized ? "serialized" : "non-serialized"} style={{ fontSize: '0.7rem' }}>
                      {result.isSerialized ? "Serialized" : "Standard"}
                    </span>
                  </div>
                  <span className="quantity opacity-50">Stock: {result.totalStock || result.quantity || 0}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ItemField;
